import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { _className, AdapterBuilder, EditableDBItemV3, EventType_Create, EventType_Update, LL_V_C, LL_V_L, ModuleFE_Dialog, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, openContent, performAction, ProtoComponent, Show, TS_CollapsableContainer, TS_EditableText, TS_Tree } from '@nu-art/thunderstorm-frontend/index';
import { arrayToMap, BadImplementationException, Filter, filterInstances, sortArray } from '@nu-art/ts-common';
import './Panel_Tags.scss';
import { DBDef_Tag, RootTag } from '@app/hcs-shared/core';
import { ICONS } from '@app/styles';
import { WarningDialog } from '../../shared-dialogs/index.js';
import { Panel_Header } from '../../shared-components/index.js';
import { handleError } from '../../../utils.js';
import { QScrollWrapper, QToastContent } from '../../q-components/index.js';
import { Dialog_MacroTagEditor } from '../../macro-tags/Dialog_MacroTagEditor/Dialog_MacroTagEditor.js';
import { ModuleFE_MacroTag } from '../../../_entity/macro-tag/index.js';
import { ConfigKeyFE_SourceTagId, ConfigKeyFE_TagsOrder, ModuleFE_Referencable, ModuleFE_Tag } from '../../../_entity.js';
import { Dialog_EditChildrenOrder } from './Dialog_EditChildrenOrder.js';
import { ModuleFE_CollectionActions } from '@nu-art/thunderstorm-frontend/modules/ModuleFE_CollectionActions';
import { ModuleFE_ConflictResolution } from '@nu-art/conflict-resolution-frontend/_modules/ModuleFE_ConflictResolution';
import { Dialog_ReferencableEditor } from '../../../_entity/referencable/Dialog_ReferencableEditor/Dialog_ReferencableEditor.js';
// ******** Functions ********
function sort(key, config, node) {
    if (!node._children)
        return;
    const sortOrder = config?.[key];
    if (sortOrder)
        node._children = sortArray(node._children.filter(item => item.type === 'tag'), child => sortOrder.indexOf(child.item._id));
    node._children.filter(item => item.type === 'tag').forEach(child => sort(child.item._id, config, child));
}
// ******** Component Data ********
const newTagGroupId = '######';
const newTagId = '$$$$$$';
export class Panel_Tags extends ProtoComponent {
    // ######################### Static #########################
    static defaultProps = {
        keys: ['selected', 'filterTags', 'tagTreeExpansion'],
    };
    treeRendererMap = {
        root: (props) => _jsx("div", { children: props.item }),
        tag: (props) => this.tagRenderer(props),
        variable: (props) => _jsx("div", { children: props.item.name }),
    };
    // ######################### Life Cycle #########################
    __onAppConfigsUpdated(...params) {
        if (params[0] === EventType_Create || params[0] === EventType_Update) {
            if (params[1].key === 'categoriesOrder')
                this.reDeriveState();
        }
    }
    __onTagsUpdated = (...params) => {
        this.reDeriveState();
    };
    __onMacroTagUpdated(...params) {
        this.forceUpdate();
    }
    deriveStateFromProps(nextProps, state) {
        state.treeExpansion = this.getQueryParam('tagTreeExpansion', { '/': true });
        state.selectedTagId = this.getQueryParam('selected', {})[DBDef_Tag.dbKey];
        if (!this.treeRendererMap)
            this.treeRendererMap = {
                root: (props) => _jsx("div", { children: props.item }),
                tag: (props) => this.tagRenderer(props),
                variable: (props) => _jsx("div", { children: props.item.name }),
            };
        const _tree = {
            type: 'root',
            item: 'root',
            _children: []
        };
        let tags = ModuleFE_Tag.cache.allMutable();
        const sourceTagId = ConfigKeyFE_SourceTagId.get();
        // Prepare all tree nodes for all tags, source should be first... FOR NOW!!
        const tagsTreeItemsMap = arrayToMap(sortArray(tags, option => option._id === sourceTagId ? 'AAA' : option.label).map(tag => {
            const item = {
                type: 'tag',
                item: tag,
            };
            return item;
        }), option => option.item._id);
        //Filter tags
        const filter = this.getQueryParam('filterTags');
        if (filter && filter.length >= 3) {
            //Extract Categories that contain the filter inside their label
            const parentSet = new Set();
            const childrenSet = new Set();
            tags.forEach((tag) => {
                if (tag.label.toLowerCase().includes(filter)) {
                    parentSet.add(tag._id);
                    childrenSet.add(tag._id);
                }
            });
            //Add Children of found tags
            tags.forEach(tag => {
                childrenSet.forEach(id => {
                    if (id && tag.tagIds.includes(id))
                        childrenSet.add(tag._id);
                });
            });
            //Add Parent of found tags
            parentSet.forEach(id => {
                const tag = ModuleFE_Tag.cache.unique(id);
                if (!tag)
                    return;
                tag.tagIds.forEach(_id => parentSet.add(_id));
            });
            const joinedSet = new Set();
            parentSet.forEach(id => joinedSet.add(id));
            childrenSet.forEach(id => joinedSet.add(id));
            tags = filterInstances(Array.from(joinedSet).map(id => tags.find(el => el._id === id)));
        }
        // Populate the children in their parent node -- build a tree
        state.tree = tags.reduce((__tree, tag) => {
            const parentTagId = tag.tagIds[tag.tagIds.length - 1];
            if (!parentTagId) {
                _tree._children?.push(tagsTreeItemsMap[tag._id]);
                return __tree;
            }
            const parentNode = tagsTreeItemsMap[parentTagId];
            (parentNode?._children || (parentNode._children = [])).push(tagsTreeItemsMap[tag._id]);
            return __tree;
        }, _tree);
        //Set tags order by config
        const tagsOrderConfig = ConfigKeyFE_TagsOrder.get();
        if (tagsOrderConfig)
            sort(RootTag, tagsOrderConfig, state.tree);
        //Set Tree Expansion
        //If the filter is sufficient && we have children in the tree
        if (filter && filter.length >= 3 && state.tree._children && state.tree._children.length < 4)
            state.tree._children.forEach((_, i) => state.treeExpansion[`/_children/${i}/`] = true);
        state.adapter = this.createAdapter(state.tree);
        return state;
    }
    // ######################### Logic #########################
    createAdapter(tree) {
        return AdapterBuilder()
            .tree()
            .multiRender(this.treeRendererMap)
            .setExpandCollapseRenderer((props) => {
            function resolveSymbol() {
                if (typeof props.item !== 'object')
                    return '';
                if (Object.keys(props.item).length === 0)
                    return '';
                if (props.node.adapter.isParent(props.item)) {
                    return _jsx(ICONS.treeCollapse.component, {});
                }
                return '';
            }
            const className = _className('node-icon', props.node.expanded ? 'expanded' : undefined);
            return _jsx("div", { className: className, style: { minWidth: '12px' }, children: resolveSymbol() });
        })
            .hideRoot()
            .setData(tree)
            .build();
    }
    onTagSelected = (path, item) => {
        if (item.type !== 'tag')
            return;
        const selected = { ...this.getQueryParam('selected', {}) };
        selected[DBDef_Tag.dbKey] = item.item._id;
        const query = { selected: selected };
        //Set tree expansion if this item can be expanded
        if (this.treeItemHasChildren(item.item._id)) {
            const treeExpansion = { ...this.getQueryParam('tagTreeExpansion', { '/': true }) };
            if (treeExpansion[path])
                delete treeExpansion[path];
            else
                treeExpansion[path] = true;
            query.tagTreeExpansion = treeExpansion;
        }
        this.setQueryParams(query);
    };
    onTagRightClick = (e, path, item) => {
        if (!item || item.type !== 'tag')
            return;
        const itemPath = e.currentTarget.getAttribute('data-path');
        const canAddChildren = item.item.tagIds?.length === 0;
        const canDeleteChildren = !this.treeItemHasChildren(item.item._id);
        ModuleFE_MouseInteractivity.showContent({
            id: 'tags__menu',
            originPos: { x: e.clientX, y: e.clientY },
            modalPos: { x: 1, y: 1 },
            content: _jsxs(Show, { children: [_jsx(Show.If, { condition: true, children: _jsx("div", { className: 'popup-action', onClick: async () => {
                                const response = await ModuleFE_CollectionActions.check.usage({
                                    dbKey: DBDef_Tag.dbKey,
                                    itemIds: [item.item._id]
                                }).executeSync();
                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                                if (response.dependencies)
                                    ModuleFE_ConflictResolution.showDependencies(response.dependencies);
                            }, children: "Show Usage" }) }), _jsx(Show.If, { condition: true, children: _jsx("div", { className: 'popup-action', onClick: () => {
                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                                this.setState({ editedTagId: item.item._id });
                            }, children: "Edit" }) }), _jsx(Show.If, { condition: true, children: _jsx("div", { className: 'popup-action', onClick: () => {
                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                                const tagId = item.item._id;
                                const referencable = ModuleFE_Referencable.cache.find(referencable => !!referencable.dbRefs?.find(ref => ref.id === tagId));
                                if (!referencable)
                                    return Dialog_ReferencableEditor.show({ context: { dbKey: DBDef_Tag.dbKey, id: tagId } });
                                Dialog_ReferencableEditor.show({ referencableId: referencable._id });
                            }, children: "Edit Referencable" }) }), _jsx(Show.If, { condition: canAddChildren, children: _jsx("div", { className: 'popup-action', onClick: () => {
                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                                if (!item._children)
                                    item._children = [];
                                item._children.unshift({
                                    type: 'tag',
                                    item: {
                                        label: '',
                                        tagIds: [item.item._id],
                                        _id: newTagId,
                                    }
                                });
                                this.setState({
                                    editedTagId: newTagId,
                                    // without creating a new adapter the tree won't re-render properly. this is an issue with ts tree
                                    adapter: this.createAdapter(this.state.tree)
                                }, () => {
                                    const expanded = { ...this.getQueryParam('tagTreeExpansion', { '/': true }) };
                                    expanded[itemPath] = true;
                                    this.setQueryParam('tagTreeExpansion', expanded);
                                });
                            }, children: "Add Tag" }) }), _jsx(Show.If, { condition: false, children: _jsx("div", { className: 'popup-action', onClick: () => Dialog_EditChildrenOrder.show(item.item._id), children: "Edit Children Order" }) }), _jsx(Show.If, { condition: canDeleteChildren, children: _jsx("div", { className: 'popup-action', onClick: () => {
                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                                WarningDialog.showDelete({
                                    title: 'Delete Tag',
                                    executeButtonText: 'Delete Tag',
                                    warningMessages: ['Delete this tag?'],
                                    onConfirm: async () => {
                                        await performAction(async () => {
                                            await ModuleFE_Tag.v1.delete(item.item).executeSync();
                                        }, {
                                            type: 'toast',
                                            successContent: _jsx(QToastContent, { content: 'Tag deleted successfully', toastType: 'success', iconKey: 'v' }),
                                            duration: 10000
                                        }, item.item.label);
                                        ModuleFE_Dialog.close();
                                    }
                                });
                            }, children: "Remove" }) })] })
        });
    };
    executeHeaderAction = (key) => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
        switch (key) {
            case 'add-tag': {
                this.state.tree._children.unshift({
                    type: 'tag',
                    item: {
                        label: '',
                        tagIds: [],
                        _id: newTagGroupId,
                    }
                });
                return this.setState({ editedTagId: newTagGroupId });
            }
            case 'add-macro-tag': {
                return Dialog_MacroTagEditor.show();
            }
            default:
                return this.logWarning(`Missing action implementation for key ${key}`);
        }
    };
    onMacroRightClick = (e, macro) => {
        ModuleFE_MouseInteractivity.showContent({
            id: 'tags__menu',
            originPos: { x: e.clientX, y: e.clientY },
            modalPos: { x: 1, y: 1 },
            content: _jsx(_Fragment, { children: _jsx("div", { className: 'popup-action', onClick: () => {
                        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                        Dialog_MacroTagEditor.show(macro._id, true);
                    }, children: "Edit Macro Tag" }) }),
        });
    };
    treeItemHasChildren = (id) => {
        const categories = ModuleFE_Tag.cache.all();
        const find = categories.find((item) => {
            return item.tagIds.includes(id);
        });
        return !!find;
    };
    // ######################### Render #########################
    tagRenderer = (props) => {
        return _jsx(TS_EditableText.Input, { className: 'node-data', renderers: {
                cancelButton: _jsx(ICONS.x.component, {}),
                saveButton: _jsx(ICONS.v.component, {}),
                resetButton: _jsx(ICONS.sync.component, {})
            }, editMode: this.state.editedTagId === props.item._id, text: props.item.label || '', onCancel: () => {
                if (props.item._id === newTagGroupId) {
                    this.state.tree._children = (this.state.tree._children?.filter(item => item.type === 'tag')).filter(item => item.item._id !== newTagGroupId);
                }
                if (props.item._id === newTagId)
                    (this.state.tree._children?.filter(item => item.type === 'tag')).forEach(item => {
                        if (item._children)
                            item._children = (item._children?.filter(item => item.type === 'tag')).filter(i => i.item._id !== newTagId);
                    });
                this.setState({ editedTagId: undefined });
            }, onTextSaved: async (label) => {
                const newTag = new EditableDBItemV3(props.item, ModuleFE_Tag);
                newTag.set('label', label);
                //Delete const id if this is a new tag or tag group
                if ([newTagId, newTagGroupId].includes(newTag.item._id))
                    delete newTag.item._id;
                //Set
                if (newTag.item._id === newTagId) {
                    const parent = this.state.tree._children.find(item => item._children?.findIndex(tag => tag.item._id === newTagId) !== -1);
                    if (!parent)
                        throw new BadImplementationException('Could not find tag group');
                    newTag.set('tagIds', [parent.item._id]);
                }
                try {
                    await newTag.save();
                    this.setState({ editedTagId: undefined });
                }
                catch (e) {
                    handleError(e);
                }
            } });
    };
    renderHeaderMenu = () => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'popup-action', onClick: () => this.executeHeaderAction('add-tag'), children: "Add Tag Group" }), _jsx("div", { className: 'popup-action', onClick: () => this.executeHeaderAction('add-macro-tag'), children: "Add Macro Tag" })] });
    };
    renderHeader() {
        return _jsx(Panel_Header, { config: { filter: this.getQueryParam('filterTags') }, onFilterChanged: (filter) => {
                this.setQueryParam('filterTags', filter);
            }, qsearchId: 'tags-area', headerClassName: 'tags-area__header', headerTail: _jsx("div", { className: 'categories-area__header__icon', children: _jsx(ICONS.more.component, { ...openContent.popUpV2.bottom({
                        id: 'tags__menu',
                        content: this.renderHeaderMenu
                    }) }) }) });
    }
    renderMessage(message) {
        return _jsx(LL_V_L, { className: 'match_parent', style: { justifyContent: 'center', alignItems: 'center' }, children: message });
    }
    renderTree() {
        const tree = this.state.tree;
        if (!tree || !tree._children.length)
            return this.renderMessage('No categories found');
        return _jsx("div", { className: 'categories-tree', children: _jsx(TS_Tree, { expanded: { ...this.state.treeExpansion }, adapter: this.state.adapter, isSelected: data => data.item?._id === this.state.selectedTagId, onNodeClicked: this.onTagSelected, onContextMenuClicked: this.onTagRightClick }) });
    }
    renderMacroTags = () => {
        const allMacroTags = ModuleFE_MacroTag.cache.all();
        let renderedMacroTags = [];
        const filter = this.getQueryParam('filterTags');
        if (!filter || filter.length < 3)
            renderedMacroTags = allMacroTags;
        else {
            const _filter = new Filter(macro => [macro.label]);
            renderedMacroTags = _filter.filterSort(allMacroTags, filter);
        }
        if (!renderedMacroTags.length)
            return;
        return _jsx(TS_CollapsableContainer, { className: 'categories-area__macro-tags', customCaret: _jsx(ICONS.advisor_v4_arrow.component, {}), headerRenderer: 'Macro Tags', containerRenderer: () => {
                return _jsx(LL_V_L, { children: renderedMacroTags.map(macroTag => {
                        return _jsx("div", { className: 'categories-area__macro-tag', onContextMenu: e => this.onMacroRightClick(e, macroTag), onClick: () => Dialog_MacroTagEditor.show(macroTag._id), children: macroTag.label }, macroTag._id);
                    }) });
            } });
    };
    render() {
        return _jsxs(LL_V_C, { className: 'categories-area', children: [this.renderHeader(), _jsxs(QScrollWrapper, { children: [this.renderMacroTags(), this.renderTree()] })] });
    }
}
//# sourceMappingURL=Panel_Tags.js.map